import { NotionMirror } from "./notion-mirror";
import { ProcessSteps } from "./process-steps";
import { RichDoc } from "./rich-doc";
import { SetupChecklist } from "./setup-checklist";
import { SlackChannelInfo } from "./slack-channel-info";
import { ToolSetup } from "./tool-setup";

type Tool = {
  toolSetupId: string;
  name: string;
  note: string;
  link: string;
  ownerEmail: string;
  state: "pending" | "configured";
  lastAskedAt: string | null;
};

type SlackChannel = {
  name: string;
  description: string | null;
};

export type ResourceContentRendererData = {
  renderType: string | null;
  contentBlocks: unknown[];
  notionPayload?: unknown[];
  staleWarning?: boolean;
  empty?: boolean;
  syncedAt?: string | null;
  sourceUrl?: string | null;
  checkedSteps?: number[];
  tools?: Tool[];
  tool?: Tool | null;
  slackChannels?: SlackChannel[];
};

type Props = {
  data: ResourceContentRendererData;
  readOnly: boolean;
  onToggleStep?: (stepIndex: number, checked: boolean) => void;
  onToggleTool?: (toolSetupId: string) => void;
  onAskOwner?: (toolSetupId: string) => void;
  askingId?: string | null;
  onRefreshNotion?: () => void;
  isRefreshingNotion?: boolean;
};

const NOOP = () => undefined;

export function ResourceContentRenderer({
  data,
  readOnly,
  onToggleStep,
  onToggleTool,
  onAskOwner,
  askingId,
  onRefreshNotion,
  isRefreshingNotion,
}: Props) {
  switch (data.renderType) {
    case "notion_mirror":
      return (
        <NotionMirror
          contentBlocks={data.contentBlocks}
          notionPayload={data.notionPayload ?? []}
          staleWarning={data.staleWarning ?? false}
          empty={data.empty ?? false}
          syncedAt={data.syncedAt ?? null}
          sourceUrl={data.sourceUrl ?? null}
          onRefresh={onRefreshNotion}
          isRefreshing={isRefreshingNotion ?? false}
        />
      );
    case "process_steps":
      return (
        <ProcessSteps
          contentBlocks={data.contentBlocks}
          progressTrackable={true}
          checkedSteps={data.checkedSteps ?? []}
          readOnly={readOnly}
          onToggleStep={onToggleStep ?? NOOP}
        />
      );
    case "setup_checklist":
      return (
        <SetupChecklist
          tools={data.tools ?? []}
          readOnly={readOnly}
          onToggle={onToggleTool ?? NOOP}
          onAskOwner={onAskOwner ?? NOOP}
          askingId={askingId ?? null}
        />
      );
    case "tool_setup":
      if ((data.tools?.length ?? 0) > 1) {
        return (
          <SetupChecklist
            tools={data.tools ?? []}
            readOnly={readOnly}
            onToggle={onToggleTool ?? NOOP}
            onAskOwner={onAskOwner ?? NOOP}
            askingId={askingId ?? null}
          />
        );
      }
      return (
        <ToolSetup
          tool={data.tool ?? null}
          readOnly={readOnly}
          onToggle={onToggleTool ?? NOOP}
          onAskOwner={onAskOwner ?? NOOP}
          askingId={askingId ?? null}
        />
      );
    case "slack_channel_info":
      return <SlackChannelInfo channels={data.slackChannels ?? []} />;
    case "rich_doc":
      return <RichDoc contentBlocks={data.contentBlocks} />;
    default:
      return <RichDoc contentBlocks={data.contentBlocks} />;
  }
}
